import { BRAND } from "@br9704/aethereum-shared";

/**
 * The CLI `--help` banner — a brand surface (docs/UI_SPEC.md §4). Black-and-white,
 * monospace, no ANSI color (color is a punctuation accent, never load-bearing).
 * The product name is routed through the single BRAND constant so the Sprint L
 * rename is a one-line change.
 */
export function helpBanner(): string {
  const n = BRAND.name;
  const rule = "  " + "─".repeat(58);
  return [
    "",
    `   ⬡  ${BRAND.logo}   ${BRAND.tagline}`,
    rule,
    "",
    "   the loop (this is what your agents do, on their own)",
    "     share_intent  →  declare_contract  →  get_team_context  →  coordinate",
    "     they publish what they're building, register interfaces, read the team's",
    "     state, and get warned before two agents collide. You keep using `claude`.",
    "",
    "   ── HOSTED PATH (zero install, no signup) ────────────────────────────────",
    `     ${n} init [--token <key>] [--no-room]        wire auto mode; makes a free room if none`,
    `            [--yes] [--no-brief]                    --yes: non-interactive · --no-brief: skip project brief`,
    `     ${n} join <code>                             join a teammate's room with their join code`,
    `     ${n} demo                                    watch two agents collide live in ~10s (no setup)`,
    `     ${n} watch [--url <base>] [--token <key>]    live ASCII room view (try this!)`,
    `     ${n} status [--json] [--fix]                 check that auto mode is working (--fix: repair hooks + .mcp.json)`,
    `     ${n} reconnect [--token <key>]               recover this project's room id (never mints a room)`,
    `     ${n} rotate                                  rotate the room key + re-wire this project`,
    `     ${n} sync-rules                              fold the team ruleset into the local AGENTS.md`,
    `     ${n} statusline install | uninstall          live team signals in your Claude Code statusline`,
    `     ${n} context                                 print context to paste into v0/Lovable/Bolt`,
    `     ${n} share <file> [--contract <name>]         share code end-to-end encrypted (opt-in)`,
    `     ${n} fetch <ref> [--out <file>]               fetch + decrypt a teammate's shared code`,
    `     ${n} verify [--confirm]                       safety-number check → compromise-resistant room`,
    `     ${n} handoff [session-id] [--to <member>]      hand a session to a teammate, E2EE (opt-in)`,
    `     ${n} handoff resume [artifact-id]              list waiting handoffs, or place one + resume`,
    `     ${n} changelog                                the team's recent changes (scan for conflicts)`,
    `     ${n} sessions [--agent --branch --since]       your local agent sessions (tokens, est cost)`,
    `     ${n} sessions search "<query>" [--json]        search your local transcripts (stays on this machine)`,
    `     ${n} sessions replay <id> [--out <path>]       one self-contained HTML replay file (never uploaded)`,
    `     ${n} sessions insights [--json]              local error taxonomy over your transcripts (what breaks most)`,
    `     ${n} usage [--week|--month] [--by-repo]        local token + cost report (estimates, local only)`,
    `     ${n} usage --compact                         a shareable fixed-width usage card (estimates, local)`,
    `     ${n} usage blocks [--json]                   5-hour block fuel gauge (burn rate, projection, reset)`,
    `     ${n} usage --publish                           send opt-in numbers-only rollups to your room`,
    `     ${n} distill [--scan] [--weekly]                find skill candidates (--weekly: PR-ready summary, est tokens saved)`,
    `     ${n} distill --publish <slug>                  preview + consent-gate publishing a drafted skill`,
    `     ${n} check [--strict]                          pre-commit contract + ruleset gate (advisory default)`,
    "                                                   ruleset rules: forbid <glob> · require <glob> -> <glob> · '- do not commit/edit <glob>'",
    `     ${n} guide                                    how to coordinate in this room (the loop)`,
    `     next step → just run '${n} init' (a free room is created, no account needed)`,
    "",
    "   ── ROOMS + AGENTS (do it all from the CLI, no website) ───────────────────",
    `     ${n} room create [name]                      make a new room (free, or under your account)`,
    `     ${n} room list [--local]                     list your rooms (--local: this machine's project → room map)`,
    `     ${n} room use <id|name>                      re-wire THIS project to another owned room`,
    `     ${n} agent add [name]                        add another agent/machine to THIS room`,
    `     ${n} invite <email> | invite --link          invite a teammate (claim the room first if anon)`,
    `     ${n} claim                                   keep an accountless room forever`,
    "",
    "   ── ACCOUNT ───────────────────────────────────────────────────────────────",
    `     ${n} login [<cli-token>]                     sign in with a token from /account`,
    `     ${n} logout                                  clear the stored account token`,
    `     ${n} whoami                                  show the active account + room`,
    "",
    "   ── LOCAL PATH (no account, you run the room server) ──────────────────────",
    `     ${n} [--room <name>] [--port <port>] [--share]   host a room, then join it`,
    `     ${n} serve [--room <name>] [--port <port>]       headless room server`,
    `     ${n} join <url> --name <you> [--guest]           join a teammate's room`,
    `     ${n} mcp --join <share-url> --name <you>         stdio MCP for native claude`,
    `     next step → ${n} --share, copy the share URL, hand it to a teammate's join`,
    "",
    "   ── ADVANCED ──────────────────────────────────────────────────────────────",
    "     Most people just run init, then use Claude Code / Cursor / Codex as normal.",
    `     ${n} run claude|codex|opencode --name <you>   optional: push teammate alerts`,
    "                                                   into a running session (needs tmux)",
    "",
    "   ── MISSION CONTROL (direct your agents — needs a hosted token) ───────────",
    `     ${n} mission                                 interactive cockpit: live room + a command bar to direct agents`,
    `     ${n} directive set "<text>"                  one standing order, top of every context`,
    `     ${n} directive clear                         lift the standing order`,
    `     ${n} prompt "<text>" [--to <handle>]         one-shot prompt (broadcast or to one)`,
    `     ${n} ticket create "<title>" [--assignee --priority --due]   open a work ticket`,
    `     ${n} ticket list | assign <id> <h> | doing <id> | done <id> | close <id>`,
    `     ${n} goal set "<text>" [--max-iterations --budget-tokens --stop]   drive the loop objective`,
    `     ${n} goal update <id> [--status --iteration --spent-tokens] | goal show`,
    "",
    "   the tools your agents share (no code ever leaves the team)",
    "     Core         share_intent · declare_contract · get_team_context",
    "     Memory       record_decision · share_plan · set_brief · search_memory",
    "     Loop         set_goal · update_goal · record_verification   (objective → budget → exit signal)",
    "     Coordination claim · release · blast_radius · send_message",
    "     Negotiation  propose_contract · respond_to_proposal · finalize_proposal",
    "     Mission Ctrl set_directive · clear_directive   (one standing order for all)",
    "",
    "   native claude (no wrapper):",
    `     claude mcp add ${n} -- npx -y ${BRAND.pkg} mcp --join "<share-url>" --name you`,
    "",
    `   status: ● not connected            docs: ~/docs            ${BRAND.repo}`,
    "",
  ].join("\n");
}

export function versionLine(version: string): string {
  return `${BRAND.logo} ${BRAND.name} v${version}`;
}
